import { useEffect, useRef } from 'react';
import { toast } from './use-toast';
import { API_BASE, getToken } from '../api/client';

/**
 * Subscribes to the backend SSE stream `/api/notifications/stream`.
 * Pops a toast for CRUD events emitted by other users/devices.
 * - Does nothing if the browser is offline or user is unauthenticated.
 * - EventSource cannot send headers, so we pass the session_token as `?token=`.
 */
export default function useLiveNotifications(enabled = true) {
  const esRef = useRef(null);
  const shownIdsRef = useRef(new Set());

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const token = getToken();
    if (!token) return;
    const url = `${API_BASE}/notifications/stream?token=${encodeURIComponent(token)}`;
    let es;
    try {
      es = new EventSource(url);
    } catch {
      return;
    }
    esRef.current = es;

    const onNotification = (ev) => {
      let payload;
      try { payload = JSON.parse(ev.data); } catch { return; }
      if (!payload || !payload.id) return;
      if (shownIdsRef.current.has(payload.id)) return;
      shownIdsRef.current.add(payload.id);
      // Skip initial backlog older than 60s to avoid spamming at page load
      const created = payload.created_at ? new Date(payload.created_at) : null;
      if (created && Date.now() - created.getTime() > 60_000) return;
      toast({
        title: payload.title || payload.message || 'Notification',
        description: payload.description || payload.text || '',
      });
    };

    es.addEventListener('notification', onNotification);
    es.onerror = () => {
      // Browser will auto-reconnect; nothing to do
    };

    return () => {
      try { es.removeEventListener('notification', onNotification); } catch (_) { /* noop */ }
      try { es.close(); } catch (_) { /* noop */ }
      esRef.current = null;
    };
  }, [enabled]);
}
